/**
 * Phase 10 R3 — useComputeJobTechPay
 *
 * Two modes:
 *   1. Per-row: compute pay for a single uncomputed ticket_jobs row.
 *   2. Bulk:    compute pay for ALL uncomputed rows on a ticket.
 *
 * STRICT NULL-ONLY FILL contract:
 *   The 3 valid tech_pay_status values are all valid computed snapshot states:
 *     • 'computed'         — flat-rate $ written
 *     • 'deferred_hourly'  — hourly mode, no $ written by design
 *     • 'no_config'        — tech assigned but no pay config; no $ written
 *
 *   This action:
 *     • fills rows where tech_pay_status IS NULL
 *     • skips rows where tech_pay_status IS NOT NULL (any of the 3 above)
 *     • NEVER re-evaluates an existing snapshot, even if the tech's config
 *       has since changed. A future "recompute pay" tool would be a separate,
 *       deliberately scoped action — not this button.
 *
 *   Additionally, rows where the parent ticket has NO assigned technician are
 *   also skipped — those rows stay NULL until a tech is assigned.
 *
 * AUTHORITY (FF-A):
 *   The `labor_engine_enabled` flag + `ui_technician_pay` exposure are
 *   UI gates only. Server authority is enforced by the
 *   `trg_enforce_labor_entitlement` trigger on `ticket_jobs` (migration
 *   `20260501120000_ff_a_labor_entitlement_trigger.sql`), which raises
 *   `insufficient_privilege` if the parent shop is not CATALYST/ENTERPRISE
 *   tier and lacks the `labor_engine` addon. A sessionStorage flag
 *   override cannot bypass that trigger.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import {
  computeJobTechPay,
  techPayColumnsForUpsert,
  type TechPayConfig,
  type TechPayMode,
} from '@/lib/labor/computeJobTechPay';

interface ComputeArgs {
  ticketId: string;
  /** When provided, only this single ticket_jobs row is touched. */
  jobId?: string;
}

export function useComputeJobTechPay() {
  const qc = useQueryClient();
  const laborEnabled = useFeatureFlag('labor_engine_enabled');
  const techPayEnabled = useExposureFlag('ui_technician_pay');

  return useMutation({
    mutationFn: async (args: ComputeArgs) => {
      // ── UI gate only. Server authority enforced by RLS trigger
      //    (trg_enforce_labor_entitlement, FF-A). ─────────────────────
      if (!laborEnabled || !techPayEnabled) {
        toast({
          title: 'Technician pay disabled',
          description: 'Technician pay is disabled for this shop.',
          variant: 'destructive',
        });
        return { skipped: true as const, written: 0 };
      }

      // Look up the parent ticket's assigned tech + shop_id.
      const { data: ticketRow, error: tErr } = await (supabase as any)
        .from('tickets')
        .select('shop_id, assigned_tech_id')
        .eq('id', args.ticketId)
        .single();
      if (tErr || !ticketRow) throw tErr ?? new Error('ticket not found');

      // No tech assigned → all rows stay NULL by design.
      if (!ticketRow.assigned_tech_id) {
        toast({
          title: 'No technician assigned',
          description: 'Assign a technician before computing pay.',
        });
        return { skipped: false as const, written: 0 };
      }

      // Resolve current effective pay config (manager-RLS gated).
      const { data: cfgRow } = await (supabase as any)
        .from('technician_compensation')
        .select('pay_mode, hourly_rate, flat_rate_per_hour')
        .eq('shop_id', ticketRow.shop_id)
        .eq('technician_id', ticketRow.assigned_tech_id)
        .is('effective_to', null)
        .order('effective_from', { ascending: false })
        .limit(1)
        .maybeSingle();
      const techPayConfig: TechPayConfig | null = cfgRow
        ? {
            pay_mode: (cfgRow.pay_mode as TechPayMode) ?? 'hourly',
            hourly_rate:
              cfgRow.hourly_rate != null ? Number(cfgRow.hourly_rate) : null,
            flat_rate_per_hour:
              cfgRow.flat_rate_per_hour != null
                ? Number(cfgRow.flat_rate_per_hour)
                : null,
          }
        : null;

      // Server-side guard: only NULL tech_pay_status rows.
      let q = (supabase as any)
        .from('ticket_jobs')
        .select(
          `ticket_id, job_id, predicted_minutes, minutes_override, sold_minutes`,
        )
        .eq('ticket_id', args.ticketId)
        .is('deleted_at', null)
        .is('tech_pay_status', null);
      if (args.jobId) q = q.eq('job_id', args.jobId);

      const { data: rows, error } = await q;
      if (error) throw error;

      let written = 0;
      for (const row of rows ?? []) {
        // R3 reference-time proxy: predicted_minutes (with minutes_override fallback).
        const refMinutes =
          row.minutes_override ?? row.predicted_minutes ?? 0;
        const computed = computeJobTechPay({
          referenceMinutes: refMinutes,
          retailSoldMinutes:
            row.sold_minutes != null ? Number(row.sold_minutes) : null,
          techPayConfig,
        });
        // Re-assert the WHERE guard on the UPDATE to defeat any race.
        const { error: updErr, count } = await (supabase as any)
          .from('ticket_jobs')
          .update(techPayColumnsForUpsert(computed), { count: 'exact' })
          .eq('ticket_id', row.ticket_id)
          .eq('job_id', row.job_id)
          .is('tech_pay_status', null);
        if (updErr) throw updErr;
        if (count) written += count;
      }
      return { skipped: false as const, written };
    },
    onSuccess: (res, args) => {
      if (res?.skipped) return;
      qc.invalidateQueries({ queryKey: ['ticket-tech-pay-lines', args.ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-jobs-for-parts', args.ticketId] });
      if (res.written > 0) {
        toast({
          title: 'Tech pay computed',
          description: `${res.written} job${res.written === 1 ? '' : 's'} updated.`,
        });
      }
    },
    onError: (e: any) => {
      toast({
        title: 'Failed to compute tech pay',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
